import {
  Flex,
  Form,
  Text,
  Stack,
  Button,
  CurrencyInput,
  FormControl,
  FormErrorMessage,
  FormLabel
} from '@homebotapp/hb-react-component-catalog'
import {
  selectEditableLoanHomeID,
  selectEditableLoanBalance,
  selectEditableLoanCanonicalID,
  selectEditableLoanEstimatedBalance
} from '../../../../store/selectors/home/loans'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import { useAppSelector } from '../../../../store/hooks'
import { formatCurrency } from '../../../../helpers/formatters'
import { cancelEditBalance, createBalance } from '../../../../actions/loan'
import { HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  current: {
    id: 'Mortgages.editBalance.todayBalance',
    defaultMessage: 'Your updated balance'
  },
  required: {
    id: 'Mortgages.editBalance.required',
    defaultMessage: 'Loan balance is required'
  },
  min: {
    id: 'Mortgages.editBalance.min',
    defaultMessage: 'Loan balance cannot be less than zero'
  },
  max: {
    id: 'Mortgages.editBalance.max',
    defaultMessage: 'Updated loan balance must be less than the remaining outstanding principal of {max}'
  },
  save: {
    id: 'Mortgages.editBalance.saveButton',
    defaultMessage: 'Save changes'
  },
  cancel: {
    id: 'Mortgages.editBalance.cancelButton',
    defaultMessage: 'Cancel'
  },
  yes: {
    id: 'Mortgages.editBalance.yesButton',
    defaultMessage: 'Yes'
  },
  no: {
    id: 'Mortgages.editBalance.noButton',
    defaultMessage: 'No'
  },
  alert: {
    id: 'Mortgages.editBalance.alert',
    defaultMessage: 'Are you sure this is accurate?'
  }
})

export interface EditBalanceFormData {
  currentBalance: number
}

export interface EditBalanceFormProps {
  showCannotIncreaseBalanceMsg: boolean
  setShowCannotIncreaseBalanceMsg: (show: boolean) => void
}

export const EditBalanceForm = ({
  showCannotIncreaseBalanceMsg,
  setShowCannotIncreaseBalanceMsg
}: EditBalanceFormProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [showAlert, setShowAlert] = useState(false)
  const homeId = useSelector(selectEditableLoanHomeID)
  const balance = useSelector(selectEditableLoanBalance)
  const saving = useAppSelector(state => state.loan.saving)
  const loanId = useSelector(selectEditableLoanCanonicalID)
  const principalLeft = useSelector(selectEditableLoanEstimatedBalance)

  return (
    <Flex gap='6' borderRadius='.5rem' justify='space-between' paddingTop='4' direction={['column', 'row']}>
      <Form<EditBalanceFormData>
        id='edit-balance-form'
        defaultValues={{
          currentBalance: Math.floor(balance && balance < principalLeft ? balance : principalLeft)
        }}
        onSubmit={data => {
          showAlert
            ? dispatch(
                createBalance({
                  homeId,
                  loanId,
                  value: data.currentBalance,
                  date: new Date().toJSON()
                })
              )
            : setShowAlert(true)
        }}
      >
        {({ formState: { errors } }) => (
          <FormControl isInvalid={!!errors?.currentBalance}>
            <FormLabel>{intl.formatMessage(MSG.current)}</FormLabel>
            <CurrencyInput
              name='currentBalance'
              // Not allowing decimals is a temp solution. Backend should accept
              // decimals...
              decimalScale={0}
              isDisabled={showAlert}
              data-qa='EditBalanceFormField'
              tracking={{
                guid: 'vqtkcXUkMzGsRSSjYEbY2s',
                ui_context: HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE,
                descriptor: MSG.current.defaultMessage
              }}
              rules={{
                validate: {
                  required: (v: string) => v?.length > 0 || intl.formatMessage(MSG.required),
                  lessThanLoanPrincipalLeft: (v: string) => {
                    if (parseInt(v) >= Math.floor(principalLeft)) {
                      setShowCannotIncreaseBalanceMsg(true)
                      return intl.formatMessage(MSG.max, { max: formatCurrency(Math.floor(principalLeft)) })
                    } else {
                      if (showCannotIncreaseBalanceMsg) {
                        setShowCannotIncreaseBalanceMsg(false)
                      }

                      return undefined
                    }
                  }
                }
              }}
            />
            <FormErrorMessage>{errors?.currentBalance?.message?.toString()}</FormErrorMessage>
          </FormControl>
        )}
      </Form>
      <Stack justifyContent='center'>
        {showAlert && <Text>{intl.formatMessage(MSG.alert)}</Text>}
        <Button
          size='sm'
          width='100%'
          type='submit'
          isLoading={saving}
          isDisabled={saving}
          colorScheme='primary'
          form='edit-balance-form'
          data-qa='EditBalanceSaveButton'
          tracking={{
            guid: 'pyb1HvQWCQVWeZ8zApgbb2',
            ui_context: HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE,
            descriptor: showAlert ? MSG.yes.defaultMessage : MSG.save.defaultMessage
          }}
        >
          {intl.formatMessage(showAlert ? MSG.yes : MSG.save)}
        </Button>
        <Button
          size='sm'
          width='100%'
          isDisabled={saving}
          data-qa='EditBalanceCancelButton'
          onClick={() => (showAlert ? setShowAlert(false) : dispatch(cancelEditBalance()))}
          tracking={{
            guid: 'tagHaf97Qx5FcWsyqfQTh4',
            ui_context: HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE,
            descriptor: showAlert ? MSG.no.defaultMessage : MSG.cancel.defaultMessage
          }}
        >
          {intl.formatMessage(showAlert ? MSG.no : MSG.cancel)}
        </Button>
      </Stack>
    </Flex>
  )
}
